import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { MultiSelect } from 'primereact/multiselect';
import { InputNumber } from 'primereact/inputnumber';
import { Button } from 'primereact/button';
import { Badge } from 'primereact/badge';
import { OverlayPanel } from 'primereact/overlaypanel';

/* ════════════════════════════════════════════════════════════════════════════
   Napredni filter spiska vozila

   Isti obrazac kao `ZaposleniFilter` — namerno, da se dva pregleda ne razilaze:
   polja stoje u POPOVERU, a ne u traci zaglavlja. Razlog je praktičan: pet
   padajućih lista u zaglavlju pojede ceo red, pretraga se prelomi, a traka
   iznad tabele gura tabelu naniže pa ona pobegne ispod okvira.

   Filtrira se NAD VEĆ UČITANIM spiskom — server vraća sva vozila firme jednim
   pozivom, pa svaka promena radi trenutno, bez odlaska na bazu. Zato i Excel
   izvoz prati filter: izvozi se isto što se vidi.
   ════════════════════════════════════════════════════════════════════════════ */

export const PRAZAN_FILTER = {
    vrsta: [],        // vrednosti iz kolone „Vrsta vozila"
    nadgradnja: [],   // vrednosti iz kolone „Vrsta nadgradnje"
    euro: [],         // vrednosti iz kolone „Euro oznaka"
    godOd: null,      // godište od (uključivo)
    godDo: null,      // godište do (uključivo)
};

const imaVrednost = (v) => v != null && String(v).trim() !== '' && String(v).trim() !== '-';

/* Vozilo prolazi filter samo ako zadovolji SVE postavljene uslove. */
export const primeniFilter = (redovi, f) => {
    if (!redovi) return redovi;
    const u = f || PRAZAN_FILTER;

    return redovi.filter(r => {
        if (u.vrsta?.length && !u.vrsta.includes(r?.['Vrsta vozila'])) return false;
        if (u.nadgradnja?.length && !u.nadgradnja.includes(r?.['Vrsta nadgradnje'])) return false;
        if (u.euro?.length && !u.euro.includes(r?.['Euro oznaka'])) return false;

        /* Godina stiže kao broj iz `YEAR()`. Vozilo BEZ godine proizvodnje
           ispada iz svakog opsega — nije „staro 0 godina". */
        if (u.godOd != null || u.godDo != null) {
            const g = Number(r?.['Godina proizvodnje']);
            if (!g) return false;
            if (u.godOd != null && g < Number(u.godOd)) return false;
            if (u.godDo != null && g > Number(u.godDo)) return false;
        }

        return true;
    });
};

/* Koliko uslova je postavljeno — stoji na dugmetu, da se sa zatvorenim panelom
   vidi da filter uopšte radi. Bez toga korisnik gleda kratak spisak i ne zna
   zašto je kratak. Opseg godišta se broji kao JEDAN uslov, ma koliko strana
   bilo popunjeno — „od 2016 do 2018" je jedan zahtev, ne dva. */
export const brojUslova = (f) => {
    const u = f || PRAZAN_FILTER;
    return (u.vrsta?.length ? 1 : 0)
         + (u.nadgradnja?.length ? 1 : 0)
         + (u.euro?.length ? 1 : 0)
         + (u.godOd != null || u.godDo != null ? 1 : 0);
};

const VozilaFilter = ({ sviRedovi, filter, postaviFilter }) => {
    const { t } = useTranslation();
    const panelRef = useRef(null);
    const podaci = sviRedovi || [];
    const f = filter || PRAZAN_FILTER;

    /* Opcije se izvode IZ PODATAKA, ne iz šifarnika: nudi se samo ono što firma
       stvarno ima, pa filter ne može da vrati prazno. */
    const opcijeIz = (kolona) => {
        const skup = new Set();
        podaci.forEach(r => { if (imaVrednost(r?.[kolona])) skup.add(r[kolona]); });
        return [...skup].sort((a, b) => String(a).localeCompare(String(b), 'sr'))
                        .map(v => ({ label: v, value: v }));
    };
    const vrstaOpcije = useMemo(() => opcijeIz('Vrsta vozila'), [podaci]);        // eslint-disable-line react-hooks/exhaustive-deps
    const nadgradnjaOpcije = useMemo(() => opcijeIz('Vrsta nadgradnje'), [podaci]); // eslint-disable-line react-hooks/exhaustive-deps
    const euroOpcije = useMemo(() => opcijeIz('Euro oznaka'), [podaci]);          // eslint-disable-line react-hooks/exhaustive-deps

    const izmeni = (delta) => postaviFilter({ ...f, ...delta });
    const ukupnoUslova = brojUslova(f);

    /* Oznaka uz naziv polja — da se sa OTVORENIM panelom odmah vidi PO ČEMU je
       filtrirano, bez čitanja svake liste. Broj je koliko je vrednosti izabrano;
       kod opsega godišta stoji kvačica, jer tu nema šta da se broji. */
    const oznaka = (broj) => (broj ? <Badge value={broj} severity="info" className="vozila-filter-badge" /> : null);

    const polja = (
        <div className="zap-filter-polja">
            <div className="zap-polje">
                <label>{t('admin.vrstaVozila')}: {oznaka(f.vrsta?.length)}</label>
                <MultiSelect value={f.vrsta} options={vrstaOpcije} display="chip"
                    style={{ width: '100%' }} maxSelectedLabels={3}
                    placeholder={t('zapFilter.sve')}
                    onChange={e => izmeni({ vrsta: e.value })} />
            </div>

            <div className="zap-polje">
                <label>{t('admin.vrstaNadgradnje')}: {oznaka(f.nadgradnja?.length)}</label>
                <MultiSelect value={f.nadgradnja} options={nadgradnjaOpcije} display="chip"
                    style={{ width: '100%' }} maxSelectedLabels={3}
                    placeholder={t('zapFilter.sve')}
                    onChange={e => izmeni({ nadgradnja: e.value })} />
            </div>

            <div className="zap-polje">
                <label>{t('admin.euroOznaka')}: {oznaka(f.euro?.length)}</label>
                <MultiSelect value={f.euro} options={euroOpcije} display="chip"
                    style={{ width: '100%' }} maxSelectedLabels={3}
                    placeholder={t('zapFilter.sve')}
                    onChange={e => izmeni({ euro: e.value })} />
            </div>

            <div className="zap-polje">
                <label>
                    {t('admin.godiste')}:{' '}
                    {(f.godOd != null || f.godDo != null) && (
                        <Badge value="✓" severity="info" className="vozila-filter-badge" />
                    )}
                </label>
                {/* Dva polja u jednom redu: opseg se čita kao jedna stvar. */}
                <div className="vozila-godiste">
                    <InputNumber value={f.godOd} useGrouping={false} min={1950} max={2100}
                        placeholder={t('admin.godisteOd')}
                        onValueChange={e => izmeni({ godOd: e.value ?? null })} />
                    <InputNumber value={f.godDo} useGrouping={false} min={1950} max={2100}
                        placeholder={t('admin.godisteDo')}
                        onValueChange={e => izmeni({ godDo: e.value ?? null })} />
                </div>
            </div>
        </div>
    );

    return (
        <div className="zap-filter">
            <div className="zap-filter-akcije">
                <Button type="button" icon="pi pi-filter" className="p-button-sm"
                    label={ukupnoUslova > 0 ? `${t('zapFilter.naslov')} (${ukupnoUslova})` : t('zapFilter.naslov')}
                    onClick={(e) => panelRef.current?.toggle(e)} />
                {ukupnoUslova > 0 && (
                    <Button type="button" icon="pi pi-filter-slash" className="p-button-sm p-button-text"
                        label={t('zapFilter.ponisti')} onClick={() => postaviFilter(PRAZAN_FILTER)} />
                )}
            </div>

            {/* `dismissable={false}` je namerno: padajuće liste unutar panela
                iscrtavaju se IZVAN njegovog DOM-a, pa bi zatvaranje na klik
                napolje gasilo ceo filter čim se otvori bilo koja lista. Zato se
                zatvara krstićem. Isto rešenje kao kod zaposlenih. */}
            <OverlayPanel ref={panelRef} className="zap-filter-panel" showCloseIcon dismissable={false}>
                {polja}
            </OverlayPanel>
        </div>
    );
};

export default VozilaFilter;
